import type { JSX } from 'react';
import { useCallback, useEffect, useState } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import type { Me } from '../auth/use-me';
import { useAgentSettings } from '../agent/use-agent';
import { now } from '@/lib/clock';
import { failureFrom } from '@/lib/result';
import { useViewport } from '@/lib/viewport';
import type { Route } from '../shell/routes';
import { useToast } from '../shell/toast';
import { useEscape } from '../shell/use-escape';
import { navigate, replaceRoute } from '../shell/use-route';
import { chatRoute, conversationIdOf } from './chat-route';
import { ArtifactPanel } from './artifact-panel';
import { conversationMeta, scopeSummary, threadTitle } from './chat-format';
import { chatLayout } from './chat-layout';
import { ChatSidebar } from './chat-sidebar';
import { Composer } from './composer';
import { Thread } from './thread';
import { threadStatus } from './thread-status';
import { useConversation, useScopes } from './use-chat-data';
import type { ConversationData } from './use-chat-data';
import { useChatSession } from './use-chat-session';
import type { ChatSession } from './use-chat-session';
import { useScope } from './use-scope';
import { useVoice } from './use-voice';

/**
 * The Chat screen, three columns in one card as the design draws it. The card
 * fills the viewport below the header (the page never scrolls on Chat); each
 * column scrolls inside it and the composer stays pinned at the bottom: the
 * conversation list, the thread with its composer, and — once an answer
 * carries one — the artifact panel. Narrower screens drop columns (chat-layout).
 */
export function ChatPage({ route, me }: { route: Route; me: Me }): JSX.Element {
  const scopes = useScopes();
  const { scope, available, toggle } = useScope(me.id, scopes.data);
  const session = useChatSession(me, scope, conversationIdOf(route));
  const current = useConversation(session.activeId);
  useMissingConversation(current);
  const viewport = useViewport();
  const [listOpen, setListOpen] = useState(false);
  const closeList = useCallback(() => setListOpen(false), []);
  useEscape(closeList);
  const model = useModel();

  const data = current.data;
  const messages = data?.messages ?? [];
  const artifactMessage = messages.find(
    (m) => m.id === session.artifactId && m.artifact !== undefined,
  );
  const artOpen = artifactMessage !== undefined;
  const layout = chatLayout(viewport, artOpen, listOpen);
  const readOnly = data !== undefined && data.conversation.owner.id !== me.id;
  // Phone: ☰ toggles the overlay. Tablet: it closes the artifact to bring the list back.
  const onMenu =
    viewport === 'phone' ? () => setListOpen(!listOpen) : () => session.showArtifact(null);

  return (
    <section
      data-screen-label="Chat"
      className="border-line bg-surf relative grid min-h-0 flex-1 animate-[rise_.4s_ease_both] grid-rows-[minmax(0,1fr)] overflow-hidden rounded-[18px] border"
      style={{ gridTemplateColumns: layout.columns }}
    >
      {layout.list === 'overlay' && (
        <div aria-hidden="true" onClick={closeList} className="absolute inset-0 z-[11]" />
      )}
      {layout.list !== 'hidden' && (
        <ChatSidebar
          meId={me.id}
          activeId={session.activeId}
          onOpen={session.open}
          onPick={closeList}
          onDeleted={(id) => id === session.activeId && replaceRoute(chatRoute(null))}
          scopes={scopes.data ?? []}
          scope={scope}
          onToggleScope={toggle}
          className={layout.list === 'overlay' ? LIST_OVERLAY : undefined}
        />
      )}
      {/* Hidden rather than unmounted on a phone, so closing the artifact returns to the same place. */}
      <div className={layout.thread ? 'contents' : 'hidden'}>
        <Thread
          key={session.epoch}
          header={{
            title: threadTitle(data?.conversation.title),
            meta: headerMeta(data, me.id, model),
            scope: scopeSummary(scope.length, available.length),
            onShowList: layout.menu ? onMenu : undefined,
          }}
          messages={messages}
          turn={session.turn}
          typing={session.typing}
          showingId={artOpen ? session.artifactId : null}
          onTyped={session.typed}
          onOpenArtifact={session.showArtifact}
          onAsk={session.ask}
          onRetry={readOnly ? undefined : session.retry}
          onSchedule={scheduleFor(me, scope)}
          askDisabled={!session.canAsk('?')}
          status={threadStatus(current, session.activeId !== null)}
          composer={<ChatComposer session={session} data={data} meId={me.id} />}
        />
      </div>
      {artifactMessage !== undefined && (
        <ArtifactPanel message={artifactMessage} onClose={() => session.showArtifact(null)} />
      )}
    </section>
  );
}

/** An admin's "Schedule this": the schedule editor, prefilled with the question and its sources. */
function scheduleFor(me: Me, scope: readonly string[]): ((text: string) => void) | undefined {
  if (me.role !== 'admin') return undefined;
  return (text) =>
    navigate({ page: 'schedules', action: 'add', query: { prompt: text, scope: scope.join(',') } });
}

/** The phone's conversation list, floated over the thread from the card's left edge. */
const LIST_OVERLAY = 'shadow-panel absolute inset-y-0 left-0 z-[12] w-[min(280px,85%)]';

/** The composer, wired to the session; read-only in someone else's conversation. */
function ChatComposer({
  session,
  data,
  meId,
}: {
  session: ChatSession;
  data: ConversationData | undefined;
  meId: string;
}): JSX.Element {
  const { draft, setDraft } = session;
  const voice = useVoice((text) => setDraft(draft === '' ? text : `${draft} ${text}`));
  const owner = data?.conversation.owner;
  return (
    <Composer
      draft={draft}
      onDraft={setDraft}
      onSend={() => session.ask(draft)}
      canSend={session.canAsk(draft)}
      busy={session.busy}
      files={session.attachments.files}
      onPick={session.attachments.add}
      onRemoveFile={session.attachments.remove}
      voice={voice}
      readOnlyOwner={owner !== undefined && owner.id !== meId ? owner.name : undefined}
    />
  );
}

/** A link to a conversation that is gone (deleted, or never ours): say so, and start a new one. */
function useMissingConversation(query: UseQueryResult<unknown>): void {
  const toast = useToast();
  const missing = query.isError && failureFrom(query.error).status === 404;
  useEffect(() => {
    if (!missing) return;
    toast('That conversation no longer exists');
    replaceRoute(chatRoute(null));
  }, [missing, toast]);
}

/** The model answering here, for a new conversation's header. */
function useModel(): string | undefined {
  const agent = useAgentSettings();
  return agent.data?.settings.model ?? agent.data?.models[0];
}

/** "ANNA · SEP 17" for a conversation; "WEB UI · <MODEL>" before the first question. */
function headerMeta(
  data: ConversationData | undefined,
  meId: string,
  model: string | undefined,
): string {
  const meta =
    data === undefined
      ? `Web UI${model === undefined ? '' : ` · ${model}`}`
      : conversationMeta(data.conversation, meId, now());
  return meta.toUpperCase();
}
